

import { palette } from './index';
import { shadeOf } from './shade';

// Works out colors for whichever series in `orderedKeyMap` don't already
// have an entry in `existing`. Returns ONLY the new entries — the caller
// merges them in (via the addColors action).
//
// orderedKeyMap: { seriesKey: { deviceID, fieldName } }, in first-seen order
//                for whatever's new this pass. Must include EVERY seriesKey
//                relevant to this call (old and new) so device hues/shade
//                counts can be reconstructed from the ones already colored.
// existing:      the current colors.allocated object — { seriesKey: { color, manual, bold, shade } }
//
// One color PER DEVICE (the "shade 0" / base hue), assigned in order of
// first appearance. If a device puts more than one field on the same
// chart, the later fields get progressively darker shades of that same
// base hue, so they're still recognizably "that device" without being
// indistinguishable from each other. A field's shade is only decided once,
// the moment it's first colored — after that it's just read back from
// storage, same as before.
export function allocateColors(orderedKeyMap, existing) {
    const updates = {};

    // Reconstruct, from what's already stored, each device's base hue (the
    // color of whichever of its fields holds shade 0) and how many shade
    // levels are already in use. Manual-only devices (no auto anchor yet)
    // are treated as having no established hue — the next new field just
    // becomes a fresh device, same as if it were brand new.
    const deviceBaseColor = {};
    const deviceMaxShade = {};
    for (const seriesKey in orderedKeyMap) {
        const entry = existing[seriesKey];
        if (!entry) continue;
        const { deviceID } = orderedKeyMap[seriesKey];
        if (!entry.manual && entry.shade === 0 && !deviceBaseColor[deviceID]) {
            deviceBaseColor[deviceID] = entry.color;
        }
        if (!entry.manual && typeof entry.shade === 'number') {
            deviceMaxShade[deviceID] = Math.max(deviceMaxShade[deviceID] ?? -1, entry.shade);
        }
    }

    let deviceHueCount = Object.keys(deviceBaseColor).length;

    for (const seriesKey in orderedKeyMap) {
        if (existing[seriesKey]) continue; // already colored (auto or manual) — leave it alone

        const { deviceID } = orderedKeyMap[seriesKey];

        if (!deviceBaseColor[deviceID]) {
            // Brand new device (or one whose only colors so far are manual
            // picks) — hand it the next unused hue in the palette.
            deviceBaseColor[deviceID] = palette[deviceHueCount % palette.length];
            deviceHueCount++;
        }

        const shade = (deviceMaxShade[deviceID] ?? -1) + 1;
        deviceMaxShade[deviceID] = shade;

        updates[seriesKey] = {
            color: shadeOf(deviceBaseColor[deviceID], shade),
            manual: false,
            bold: false,
            shade,
        };
    }

    return updates;
}